/**
 * Sign-in with Amazon Cognito (D30), talking to Cognito's own endpoint from
 * the browser — no SDK. The flow is "email me a code": we ask Cognito to
 * start a USER_AUTH session preferring EMAIL_OTP, the person types the code,
 * Cognito hands back tokens. The ID token goes to our API on every call;
 * the refresh token quietly renews it for 30 days — until sign-out, which
 * revokes it (see signOut).
 */

export interface AuthConfig {
  mode: 'cognito' | 'off';
  region?: string;
  userPoolId?: string;
  clientId?: string;
}

interface StoredTokens {
  idToken: string;
  refreshToken: string;
  /** ms since epoch when the ID token stops being accepted. */
  expiresAt: number;
  email: string;
}

const KEY = 'openmd.auth';
let config: AuthConfig | null = null;
let tokens: StoredTokens | null = load();

export function configure(c: AuthConfig) {
  config = c;
}
export function isConfigured(): boolean {
  return config?.mode === 'cognito';
}
export function signedInEmail(): string | null {
  return tokens?.email ?? null;
}

/** A valid ID token for the API, renewing it first if it is about to expire; null when nobody is signed in. */
export async function idToken(): Promise<string | null> {
  if (!isConfigured() || !tokens) return null;
  if (Date.now() < tokens.expiresAt - 60_000) return tokens.idToken;
  try {
    const r = await cognito('InitiateAuth', { AuthFlow: 'REFRESH_TOKEN_AUTH', ClientId: config!.clientId, AuthParameters: { REFRESH_TOKEN: tokens.refreshToken } });
    const a = r.AuthenticationResult as { IdToken: string; ExpiresIn: number };
    tokens = { ...tokens, idToken: a.IdToken, expiresAt: Date.now() + a.ExpiresIn * 1000 };
    save(tokens);
    return tokens.idToken;
  } catch {
    signOut();
    return null;
  }
}

/** Step 1: ask Cognito to email a code. Returns the session handle step 2 needs. */
export async function startSignIn(email: string): Promise<{ session: string; destination: string }> {
  const r = await cognito('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: config!.clientId,
    AuthParameters: { USERNAME: email.trim().toLowerCase(), PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  });
  if (r.ChallengeName !== 'EMAIL_OTP') throw new Error(`This login cannot use an emailed code (${String(r.ChallengeName ?? 'no challenge')}). Ask your scheduler.`);
  const params = (r.ChallengeParameters ?? {}) as Record<string, string>;
  return { session: String(r.Session), destination: params.CODE_DELIVERY_DESTINATION ?? email };
}

/** Step 2: hand the code back; on success the tokens are kept on this device. */
export async function finishSignIn(email: string, session: string, code: string): Promise<void> {
  const r = await cognito('RespondToAuthChallenge', {
    ChallengeName: 'EMAIL_OTP',
    ClientId: config!.clientId,
    Session: session,
    ChallengeResponses: { USERNAME: email.trim().toLowerCase(), EMAIL_OTP_CODE: code.trim() },
  });
  const a = r.AuthenticationResult as { IdToken: string; RefreshToken: string; ExpiresIn: number } | undefined;
  if (!a) throw new Error('Cognito did not return a session. Try again.');
  tokens = { idToken: a.IdToken, refreshToken: a.RefreshToken, expiresAt: Date.now() + a.ExpiresIn * 1000, email: email.trim().toLowerCase() };
  save(tokens);
}

/**
 * Forget the session on this device and tell Cognito to stop honouring its
 * refresh token (D37), so a sign-out ends the session everywhere the token
 * could have been used, not just in this browser. The ID token already
 * issued lapses on its own within the hour. Best effort: if Cognito cannot
 * be reached the device is still signed out.
 */
export function signOut(): void {
  const t = tokens;
  tokens = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing stored */
  }
  if (t && config?.mode === 'cognito') void cognito('RevokeToken', { Token: t.refreshToken, ClientId: config.clientId }).catch(() => undefined);
}

/** Cognito's error codes, in words a physician can act on. */
export class CognitoError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
  }
}

async function cognito(target: 'InitiateAuth' | 'RespondToAuthChallenge' | 'RevokeToken', body: Record<string, unknown>): Promise<Record<string, unknown>> {
  if (!config?.region) throw new Error('Login is not configured');
  const res = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { __type?: string; message?: string } & Record<string, unknown>;
  if (!res.ok) {
    const code = (data.__type ?? 'Unknown').split('#').pop()!;
    throw new CognitoError(code, friendly(code, data.message));
  }
  return data;
}

function friendly(code: string, raw?: string): string {
  // Cognito allows 15 minutes between sending a code and typing it; after that the session is gone.
  if (code === 'NotAuthorizedException' && /session/i.test(raw ?? '')) return 'That code took too long — they expire after 15 minutes. Start again and we will send a fresh one.';
  switch (code) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return "We couldn't find a login for that email address. Check the spelling, or ask your scheduler to add you.";
    case 'CodeMismatchException':
      return "That code isn't right. Check the email and try again.";
    case 'ExpiredCodeException':
      return 'That code has expired. Start again to get a new one.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts for now. Wait a few minutes and try again.';
    case 'UserNotConfirmedException':
      return 'This login has not been activated yet. Ask your scheduler.';
    default:
      return raw ? `${raw} (${code})` : `Sign-in failed (${code})`;
  }
}

function load(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as StoredTokens;
    return t && typeof t.idToken === 'string' && typeof t.refreshToken === 'string' ? t : null;
  } catch {
    return null;
  }
}
function save(t: StoredTokens) {
  try {
    localStorage.setItem(KEY, JSON.stringify(t));
  } catch {
    /* private mode: the session lasts until the tab closes */
  }
}
